import { Controller } from "@hotwired/stimulus";

/**
 * Controller for executing async actions (like POST/DELETE requests) without form submission.
 * Useful for buttons inside forms that need to trigger independent actions.
 *
 * Usage:
 *   <div data-controller="async-action">
 *     <button
 *       type="button"
 *       data-action="click->async-action#execute"
 *       data-async-action-url-param="/some/path"
 *       data-async-action-method-param="POST"
 *       data-async-action-confirm-param="Are you sure?"
 *     >
 *       Do Something
 *     </button>
 *   </div>
 */
export default class extends Controller {
  async execute(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget as HTMLButtonElement;
    const url = button.dataset.asyncActionUrlParam;
    const method = button.dataset.asyncActionMethodParam || "POST";
    const confirmMessage = button.dataset.asyncActionConfirmParam;

    if (!url) {
      console.error("async-action: No URL provided");
      return;
    }

    // Show confirmation dialog if specified
    if (confirmMessage && !confirm(confirmMessage)) {
      return;
    }

    // Disable button and show loading state
    const originalContent = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `<span class="loading loading-spinner loading-xs"></span> Processing...`;

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Accept": "text/vnd.turbo-stream.html, text/html, application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        credentials: "same-origin",
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type") || "";

        if (contentType.includes("text/vnd.turbo-stream.html")) {
          // Let Turbo handle the stream response
          const html = await response.text();
          Turbo.renderStreamMessage(html);
        } else if (contentType.includes("text/html")) {
          // Replace content if HTML response
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newContent = doc.body.firstElementChild;
          if (newContent) {
            this.element.replaceWith(newContent);
          }
        }
      } else {
        console.error("async-action: Request failed", response.status);
        // Restore button on error
        button.disabled = false;
        button.innerHTML = originalContent;
      }
    } catch (error) {
      console.error("async-action: Request error", error);
      // Restore button on error
      button.disabled = false;
      button.innerHTML = originalContent;
    }
  }

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta?.getAttribute("content") || "";
  }
}

// Make Turbo available for TypeScript
declare const Turbo: {
  renderStreamMessage(html: string): void;
};
